import { useMemo, useState } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Textarea,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { FiCheckCircle } from "react-icons/fi";
import CDS from "design-system";

import UserSelectField from "components/FormElements/Fields/SelectFields/UserSelectField";
import NewHireJourneySingleSelect from "components/Form/Select/NewHireJourneySingleSelect";
import { useNewHiresListQuery } from "features/NewHireJourney/hooks";
import type { NewHireJourney } from "models/v2/newHireJourney";
import type { User } from "features/User/models";
import type { Task } from "features/Resources/Task/models";
import { isTaskOpen } from "features/Resources/Task/helpers";
import { useAssignForm } from "../hooks";
import type { CustomForm } from "../types";

const DEFAULT_TITLE = "Fill out this form";

interface AssignFormPanelProps {
  form: CustomForm;
  isOpen: boolean;
  onClose: () => void;
  existingAssignments: Task[];
}

export default function AssignFormPanel({ form, isOpen, onClose, existingAssignments }: AssignFormPanelProps) {
  // UserSelectField expects an untyped react-hook-form Control; this form has no
  // schema of its own — assignee selection is tracked separately via onChangeCallback.
  const { control, formState: { errors }, reset } = useForm();
  const [assignee, setAssignee] = useState<User | undefined>(undefined);
  const [newHireJourneyId, setNewHireJourneyId] = useState<string | undefined>(undefined);
  const [showJourneySelector, setShowJourneySelector] = useState(false);
  const [name, setName] = useState(DEFAULT_TITLE);
  const [message, setMessage] = useState("");
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/forms/${form.uuid}`;

  // Best-effort: lists every new hire journey rather than filtering to the selected
  // person's journeys. TODO: swap in a per-user journey lookup once one exists.
  const { data: newHireJourneys } = useNewHiresListQuery("", { enabled: showJourneySelector }) as {
    data: NewHireJourney[] | undefined;
  };

  const { mutate: assign, isPending } = useAssignForm(form.id);

  const duplicateWarning = useMemo(() => {
    if (!assignee) return null;
    const openAssignment = existingAssignments.find(
      (task) => String(task.assigneeId) === String(assignee.id) && isTaskOpen(task),
    );
    return openAssignment ? assignee.fullName : null;
  }, [assignee, existingAssignments]);

  const resetFormFields = () => {
    reset();
    setAssignee(undefined);
    setNewHireJourneyId(undefined);
    setShowJourneySelector(false);
    setName(DEFAULT_TITLE);
    setMessage("");
  };

  // Closing always resets to a blank form — the confirmation never lingers.
  const handleClose = () => {
    resetFormFields();
    setAssignedTo(null);
    setCopied(false);
    onClose();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
  };

  const handleSubmit = () => {
    if (!assignee) return;
    const assignedName = assignee.fullName;
    assign(
      {
        name: name.trim() || DEFAULT_TITLE,
        description: message.trim() || undefined,
        assigneeId: assignee.id,
        taskAction: "complete_form",
        customFormId: form.id,
        ...(newHireJourneyId && { newHireJourneyId }),
      },
      {
        onSuccess: () => {
          setAssignedTo(assignedName);
          resetFormFields();
        },
      },
    );
  };

  const isValid = !!assignee && name.trim().length > 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton mt="6px" />
        <ModalHeader fontSize="md" fontWeight="bold" borderBottomWidth="1px">
          Assign form
        </ModalHeader>
        <ModalBody pt={4}>
          {assignedTo ? (
            <Stack spacing={4} py={1}>
              <HStack spacing={2}>
                <Box as={FiCheckCircle} color="green.500" boxSize={5} />
                <CDS.Text16 fontWeight="bold">Assigned to {assignedTo}</CDS.Text16>
              </HStack>
              <CDS.Text14 color="content.secondary">
                Share this link so they can fill it out — opening it takes them straight to their form.
              </CDS.Text14>
              <HStack spacing={0} borderWidth="1px" borderColor="border.default" borderRadius="md" overflow="hidden">
                <CDS.Text12 flex="1" minW={0} px={3} py={2} isTruncated color="content.secondary">
                  {shareUrl}
                </CDS.Text12>
                <CDS.Button
                  intent={CDS.Button.intent.indigo}
                  variant={CDS.Button.variant.outline}
                  size="sm"
                  onClick={handleCopy}
                >
                  {copied ? "Copied" : "Copy"}
                </CDS.Button>
              </HStack>
            </Stack>
          ) : (
          <Stack spacing={4}>
            <UserSelectField
              name="assignee"
              label="Assign to"
              control={control}
              errors={errors}
              required
              onChangeCallback={(user) => setAssignee(user)}
            />

            {duplicateWarning && (
              <Box bg="orange.50" borderWidth="1px" borderColor="orange.200" borderRadius="md" px={3} py={2}>
                <CDS.Text12 color="orange.700">
                  {duplicateWarning} already has this form assigned — assign again anyway?
                </CDS.Text12>
              </Box>
            )}

            <FormControl>
              <FormLabel fontSize="14px">Title</FormLabel>
              <Input value={name} onChange={(e) => setName(e.target.value)} size="sm" />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="14px">Message (optional)</FormLabel>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a note for the person filling this out"
                size="sm"
                resize="vertical"
                rows={3}
              />
            </FormControl>

            {!showJourneySelector ? (
              <Box alignSelf="flex-start">
                <CDS.Button
                  intent={CDS.Button.intent.primary}
                  variant={CDS.Button.variant.ghost}
                  size="sm"
                  onClick={() => setShowJourneySelector(true)}
                >
                  + Attach to a journey (optional)
                </CDS.Button>
              </Box>
            ) : (
              <FormControl>
                <FormLabel fontSize="14px">New hire journey</FormLabel>
                <NewHireJourneySingleSelect
                  newHireJourneys={newHireJourneys || []}
                  selectedNewHireJourneyId={newHireJourneyId || null}
                  onChange={(id) => setNewHireJourneyId(id as string | undefined)}
                  placeholder="Select a new hire journey"
                  isClearable
                  useBasicStyles
                />
              </FormControl>
            )}
          </Stack>
          )}
        </ModalBody>
        <ModalFooter borderTopWidth="1px">
          {assignedTo ? (
            <HStack spacing={3}>
              <CDS.Button
                intent={CDS.Button.intent.indigo}
                variant={CDS.Button.variant.solid}
                size="sm"
                onClick={handleClose}
              >
                Done
              </CDS.Button>
            </HStack>
          ) : (
            <HStack spacing={3}>
              <CDS.Button
                intent={CDS.Button.intent.primary}
                variant={CDS.Button.variant.ghost}
                size="sm"
                onClick={handleClose}
              >
                Cancel
              </CDS.Button>
              <CDS.Button
                intent={CDS.Button.intent.indigo}
                variant={CDS.Button.variant.solid}
                size="sm"
                onClick={handleSubmit}
                loading={isPending}
                disabled={!isValid}
              >
                Assign
              </CDS.Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
